import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import Icon from 'icons/Icon'
import './styles/index.scss'
import { GAME_FILTER_SUPPORT_TYPE } from '../../../constants/uiConfig'
import { expandProvidersWithStudios } from '../../../utils/provider'

/**
 * Component for displaying and managing game filter selections.
 * Handles provider, studio, rating, and theme filters with dropdown functionality for studios.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.selectedFilter - Currently selected filters
 * @param {string[]} props.selectedFilter.provider - Array of selected provider names
 * @param {number[]} props.selectedFilter.studio - Array of selected studio IDs
 * @param {string[]} props.selectedFilter.rating - Array of selected ratings
 * @param {Function} props.setSelectedFilter - Callback to update selected filters
 * @param {Object} props.languageData - Internationalization data for UI text
 * @param {string} [props.languageData.noStudios] - Text to show when no studios are available
 * @param {string} [props.languageData.reset] - Text for reset button
 *
 * @returns {JSX.Element} Rendered filter selection component
 */
const GameFiltersSelection = ({
  selectedFilter,
  setSelectedFilter,
  languageData
}) => {
  const [maxVisibleFilters, setMaxVisibleFilters] = useState(10)
  const { categoryStudioList = [], providers = [], studiosByOrder, studios } = useSelector((state) => state.casinoMenu)
  const general = useSelector((state) => state?.uiConfig?.general)
  const { gameFilterSupportType = 'PROVIDER' } = general || {}

  const providersWithStudios = useMemo(() => expandProvidersWithStudios(providers), [providers?.length])

  const providerDisplayNameMap = useMemo(() => {
    const map = {}
    providersWithStudios?.forEach(provider => {
      map[provider?.name] = provider?.providerDisplayName
    })
    return map
  }, [providersWithStudios])

  const studioDisplayNameMap = useMemo(() => {
    const map = {}
    studiosByOrder?.forEach(studio => {
      map[studio?.name] = studio?.studioDisplayName
    })
    return map
  }, [studiosByOrder])

  // studioId -> studioName (from a category-scoped list)
  const studioNameById = useMemo(() => {
    const map = {}
    // Primary source: category-scoped list
    studios.forEach(s => { if (s?.id != null && s?.name) map[s.id] = s.name })
    // Fallback/enrichment: providers[].studios
    providers?.forEach(provider => {
      const list = Array.isArray(provider?.TenantGameStudios) ? provider.TenantGameStudios : []
      list.forEach(({ GameStudio = {}, id }) => {
        if (GameStudio?.id != null && GameStudio?.name && map[id] == null) {
          map[id] = GameStudio?.displayName || GameStudio.name
        }
      })
    })
    return map
  }, [studios, providers])

  // studioId -> providerName (from providers with embedded studios)
  const studioProviderById = useMemo(() => {
    const map = {}
    providers?.forEach(provider => {
      const list = Array.isArray(provider?.TenantGameStudios) ? provider.TenantGameStudios : []
      list.forEach(({ id }) => {
        if (id != null) map[id] = provider?.name
      })
    })
    return map
  }, [providers])

  // providerName -> allowed studio IDs (respect categoryStudioList restrictions)
  const providerAllowedStudiosByName = useMemo(() => {
    const allowed = new Set((studios || []).map(studio => studio?.id).filter(id => id != null))
    const map = {}
    providers?.forEach(provider => {
      const allIds = (Array.isArray(provider?.TenantGameStudios) ? provider.TenantGameStudios : [])
        .map(({ id }) => id)
        .filter(id => id != null)
      map[provider?.name] = allowed.size ? allIds.filter(id => allowed.has(id)) : allIds
    })
    return map
  }, [providers, studios])

  // Group selected studios by providerName
  const studiosGroupedByProvider = useMemo(() => {
    const groups = {}
      ; (selectedFilter?.studio || []).forEach(id => {
      const providerName = studioProviderById[id]
      if (!providerName) return
      if (!groups[providerName]) groups[providerName] = []
      groups[providerName].push(id)
    })
    return groups
  }, [selectedFilter?.studio, studioProviderById])

  // Create a combined list of all filter items for overflow handling
  const allFilterItems = useMemo(() => {
    const items = []

    // Add provider filters (fully selected providers)
    if (selectedFilter?.provider?.length > 0) {
      selectedFilter.provider.forEach((providerName, index) => {
        const allowedStudioIds = providerAllowedStudiosByName[providerName] || []
        items.push({
          type: 'provider',
          key: `provider-${providerName}-${index}`,
          providerName,
          providerDisplayName: providerDisplayNameMap[providerName],
          allowedStudioIds,
          isFullProvider: true
        })
      })
    }

    // Add rating filters
    if (selectedFilter?.rating?.length > 0) {
      selectedFilter.rating.forEach((rating, index) => {
        items.push({
          type: 'rating',
          key: `rating-${rating}-${index}`,
          value: rating
        })
      })
    }

    if (gameFilterSupportType !== GAME_FILTER_SUPPORT_TYPE.STUDIO) {
      // Add partial provider filters (studios grouped by provider)
      Object.keys(studiosGroupedByProvider).forEach((providerName) => {
        // Skip if this provider is already fully selected
        if ((selectedFilter?.provider || []).includes(providerName)) return
        const studioIds = studiosGroupedByProvider[providerName]
        if (!studioIds?.length) return

        items.push({
          type: 'provider',
          key: `provider-studio-${providerName}`,
          providerName,
          studioIds,
          isFullProvider: false
        })
      })
    } else if (gameFilterSupportType === GAME_FILTER_SUPPORT_TYPE.STUDIO) {
      selectedFilter.studio.forEach((studioName, index) => {
        const _studioName = studioNameById[studioName] || []
        items.push({
          type: 'studio',
          key: `studio-${_studioName}-${index}`,
          studioName: studioDisplayNameMap[_studioName] || _studioName,
          value: studioName
        })
      })
    }

    return items
  }, [selectedFilter, providerAllowedStudiosByName, studiosGroupedByProvider])

  // Calculate visible items and overflow count
  const { visibleItems, overflowCount } = useMemo(() => {
    const totalItems = allFilterItems.length
    if (totalItems <= maxVisibleFilters) {
      return { visibleItems: allFilterItems, overflowCount: 0 }
    }

    const visible = allFilterItems.slice(0, maxVisibleFilters) // Reserve one slot for overflow
    const overflow = totalItems - maxVisibleFilters

    return { visibleItems: visible, overflowCount: overflow }
  }, [allFilterItems, maxVisibleFilters])

  // Local state to toggle a single provider studio dropdown
  const [openProvider, setOpenProvider] = useState(null)
  const openDropdownRef = useRef(null)
  const toggleOpen = (providerName) => setOpenProvider(prev => (prev === providerName ? null : providerName))

  // Refs for measuring widths
  const containerRef = useRef(null)
  const appliedFiltersRef = useRef(null)
  const clearFilterRef = useRef(null)
  const overflowFilterRef = useRef(false)

  // Close on the outside click
  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (openDropdownRef.current && !openDropdownRef.current.contains(e.target)) {
        setOpenProvider(null)
      }
    }
    document.addEventListener('click', handleDocumentClick)
    return () => document.removeEventListener('click', handleDocumentClick)
  }, [])

  // Handle max visible filters
  useEffect(() => {
    const calculateMaxVisible = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth

        const filterBlocks = containerRef.current.querySelectorAll('.filter-block')

        let totalFilterWidth = 0
        filterBlocks.forEach((block, index) => {
          const blockWidth = block.offsetWidth
          const blockWidthWithGap = blockWidth + 10
          totalFilterWidth += blockWidthWithGap
        })

        if (appliedFiltersRef.current && clearFilterRef.current) {
          const clearFilterWidth = clearFilterRef.current.offsetWidth

          if (totalFilterWidth > containerWidth - clearFilterWidth && !overflowFilterRef.current) {
            overflowFilterRef.current = true
            const reduceBy = totalFilterWidth - (containerWidth - clearFilterWidth) > 40 ? 2 : 1
            setMaxVisibleFilters((filterBlocks.length - reduceBy) > 0 ? (filterBlocks.length - reduceBy) : 1)
          }
        }
      }
    }

    calculateMaxVisible()

    const handleResize = () => {
      calculateMaxVisible()
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [allFilterItems, visibleItems, overflowCount])

  // Helper function to render a filter item
  const renderFilterItem = (item) => {
    const isOpen = openProvider === item.providerName

    if (item.type === 'studio') {
      return (
        <a href={() => false} className='filter-block' key={item.key}>
          <span className='text'>{item.studioName}</span>
          <Icon
            name='xmark'
            className='close-icon'
            onClick={() => {
              setSelectedFilter(prevSelectedFilter => ({
                ...prevSelectedFilter,
                studio: (prevSelectedFilter?.studio || []).filter(id => id !== item.value)
              }))
            }}
          />
        </a>
      )
    }

    if (item.type === 'provider') {
      const removeAllForProvider = () => {
        if (item.isFullProvider) {
          const allowedSet = new Set(item.allowedStudioIds)
          setSelectedFilter(prevSelectedFilter => ({
            ...prevSelectedFilter,
            provider: (prevSelectedFilter?.provider || []).filter(provider => provider !== item.providerName),
            studio: (prevSelectedFilter?.studio || []).filter(id => !allowedSet.has(id))
          }))
        } else {
          setSelectedFilter(prevSelectedFilter => ({
            ...prevSelectedFilter,
            studio: (prevSelectedFilter?.studio || []).filter(id => !item.studioIds.includes(id))
          }))
        }
      }

      const removeStudioFromFull = (studioId) => {
        const allowedSet = new Set(item.allowedStudioIds)
        const remaining = item.allowedStudioIds.filter(id => id !== studioId)
        setSelectedFilter(prevSelectedFilter => {
          const nextStudioSet = new Set(prevSelectedFilter?.studio || [])
          remaining.forEach(id => nextStudioSet.add(id))
          nextStudioSet.delete(studioId)
          return {
            ...prevSelectedFilter,
            provider: (prevSelectedFilter?.provider || []).filter(provider => provider !== item.providerName),
            studio: Array.from(nextStudioSet).filter(id => !Number.isNaN(id) && (!allowedSet.has(id) || remaining.includes(id)))
          }
        })
      }

      const removeStudio = (studioId) => {
        setSelectedFilter(prevSelectedFilter => ({
          ...prevSelectedFilter,
          studio: (prevSelectedFilter?.studio || []).filter(id => id !== studioId)
        }))
      }

      const studioIds = item.isFullProvider ? item.allowedStudioIds : item.studioIds || []

      return (
        <span className='filter-block' key={item.key}>
          <span
            className='text'
            onClick={(e) => {
              e.stopPropagation()
              toggleOpen(item.providerName)
            }}
          >
            <span>{item?.providerDisplayName || item.providerName || item.studioName}</span>
            <span className='selected-studios-count'>{studioIds.length}</span>
          </span>
          <Icon name='xmark' className='close-icon' onClick={removeAllForProvider} />
          {isOpen && (
            <div
              className='provider-studios-dropdown'
              ref={openDropdownRef}
            >
              {studioIds.length > 0 && studioIds.map((studioId) => (
                <label
                  key={`dropdown-studio-${item.providerName}-${studioId}`}
                  className='provider-studios-dropdown--item'
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (item.isFullProvider) {
                      removeStudioFromFull(studioId)
                    } else {
                      removeStudio(studioId)
                    }
                  }}
                >
                  <span
                    className='provider-checkbox'
                    role='checkbox'
                    aria-checked='true'
                  >
                    <Icon name='casino-filter-checked' height='18px' width='18px' />
                  </span>
                  <span>
                    {studioNameById[studioId] || studioId}
                  </span>
                </label>
              ))}
              {studioIds.length === 0 && (
                <div className='provider-studios-dropdown--no-studios'>
                  {languageData?.noStudios || 'No Studios'}
                </div>
              )}
            </div>
          )}
        </span>
      )
    }

    if (item.type === 'rating') {
      return (
        <a href={() => false} className='filter-block' key={item.key}>
          <span className='text'>{item.value}</span>
          <Icon
            name='xmark'
            className='close-icon'
            onClick={() => {
              setSelectedFilter(prevSelectedFilter => ({
                ...prevSelectedFilter,
                rating: (prevSelectedFilter?.rating || []).filter(ele => ele !== item.value)
              }))
            }}
          />
        </a>
      )
    }

    return null
  }

  return (
    <>
      {(selectedFilter?.provider?.length > 0 || selectedFilter?.rating?.length > 0 || (selectedFilter?.studio?.length > 0)) &&
        <div className='filter-blocks-wrapper' ref={containerRef}>
          <div className='filter-blocks-wrapper--applied-filters' ref={appliedFiltersRef}>
            {visibleItems.map(renderFilterItem)}

            {overflowCount > 0 && (
              <span className='filter-block filter-block--overflow'>
                <span className='text'>
                  +{overflowCount}
                </span>
              </span>
            )}
          </div>
          <button
            ref={clearFilterRef}
            onClick={() => {
              setSelectedFilter(prevSelectedFilter => (
                {
                  ...prevSelectedFilter,
                  provider: [],
                  rating: [],
                  studio: []
                }))
            }}
            href={() => false}
            className='clear-filter btn__content_only'
          >
            <span className='text'>{languageData?.resetFilter || 'Reset Filters'}</span>
          </button>
        </div>}
    </>
  )
}

export default GameFiltersSelection
